import { Date_toDateString, Date_toDateTimeString } from "../../../common/dateUtils";
import { Location, WorkOrigin } from "../../../common/enums";
import { FormState_PopulateErrorMessages } from "../../../common/formStateUtils";
import { String_removePrefix } from "../../../common/stringUtils";
import ParticipantsList, {
    ParticipantListItem,
    ParticipantListItem_default
} from "../../../components/Forms/Permits/ParticipantsList";
import DivingPermitRequest from "../../requests/DivingPermitRequest";
import Errors from "../../responses/Errors";
import FormElement, { formElement } from "../FormElement";
import PersonFormElement, {
    PersonFormElement_default,
    PersonFormElement_toPerson,
    PersonFormElement_populateErrorMessages,
    PersonFormElements_populateErrorMessages
} from "../PersonFormElement";
import PositionPickerRow, {
    PositionPickerRow_default,
    PositionPickerRow_toPosition,
    PositionPickerRows_populateErrorMessages
} from "./PositionPickerRow";

type DivingPermitFormState = {
    remindMe: FormElement<boolean>;
    remindMeDate: FormElement<Date>;
    startDate: FormElement<Date>;
    endDate: FormElement<Date>;
    adminComment: FormElement<string>;
    description: FormElement<string>;
    positions: PositionPickerRow[];
    persons: ParticipantListItem[];
    expirationReminderEmail: FormElement<string>;
    expirationReminderPhoneNumber: FormElement<string>;
    entrepreneur: FormElement<string>;
    VHFIsAvailable: FormElement<boolean>;
    divingLeader: PersonFormElement;
    projectLeader: PersonFormElement;
    divingLocation: FormElement<Location>;
    divingFrom: FormElement<WorkOrigin>;
    energyHarbourSequenceIdentifier: FormElement<string>;
    divingCompanyNr: FormElement<string>;

    apmtInformationConfirmationSignature: FormElement<string>;
    apmtSignatureNameClarification: FormElement<string>;
};

export default DivingPermitFormState;

export function DivingPermitFormState_default(): DivingPermitFormState {
    const defaultFormState: DivingPermitFormState = {
        adminComment: formElement(""),
        description: formElement(""),
        endDate: formElement(new Date()),
        entrepreneur: formElement(""),
        expirationReminderEmail: formElement(""),
        expirationReminderPhoneNumber: formElement(""),
        positions: [PositionPickerRow_default()],
        divingFrom: formElement(WorkOrigin.Quay),
        remindMe: formElement(false),
        remindMeDate: formElement(new Date()),
        startDate: formElement(new Date()),
        VHFIsAvailable: formElement(false),
        divingLocation: formElement(Location.General),
        persons: [ParticipantListItem_default()],
        divingLeader: PersonFormElement_default(),
        projectLeader: PersonFormElement_default(),
        energyHarbourSequenceIdentifier: formElement(""),
        apmtInformationConfirmationSignature: formElement(""),
        apmtSignatureNameClarification: formElement(""),
        divingCompanyNr: formElement("")
    };

    return defaultFormState;
}

export function DivingPermitFormState_toRequest(formState: DivingPermitFormState): DivingPermitRequest {
    const request: DivingPermitRequest = {
        adminComment: formState.adminComment.value,
        description: formState.description.value,
        divingFrom: formState.divingFrom.value,
        divingLeader: PersonFormElement_toPerson(formState.divingLeader),
        divingLocation: formState.divingLocation.value,
        endDate: Date_toDateTimeString(formState.endDate.value),
        entrepreneur: formState.entrepreneur.value,
        expirationReminderEmail: formState.expirationReminderEmail.value,
        expirationReminderPhoneNumber: formState.expirationReminderPhoneNumber.value,
        energyHarbourSequenceIdentifier: formState.energyHarbourSequenceIdentifier.value,
        persons: formState.persons.map(PersonFormElement_toPerson),
        positions: formState.positions.map(PositionPickerRow_toPosition),
        projectLeader: PersonFormElement_toPerson(formState.projectLeader),
        remindMe: formState.remindMe.value,
        remindMeDate: Date_toDateString(formState.remindMeDate.value),
        startDate: Date_toDateTimeString(formState.startDate.value),
        VHFIsAvailable: formState.VHFIsAvailable.value,
        divingCompanyNr: formState.divingCompanyNr.value,
        apmtInformationConfirmationSignature: formState.apmtInformationConfirmationSignature.value,
        apmtSignatureNameClarification: formState.apmtSignatureNameClarification.value
    };

    return request;
}

export function DivingPermitFormState_populateErrorMessages(
    formState: DivingPermitFormState,
    errors: Errors
): DivingPermitFormState {
    const errorKeys = Object.keys(errors);

    const positionsErrorKeys = errorKeys.filter((e) => e.startsWith("Positions"));
    const positionErrors = Object.fromEntries(
        positionsErrorKeys.map((k) => [String_removePrefix(k, "Positions"), errors[k]])
    );

    const personErrorKeys = errorKeys.filter((e) => e.startsWith("Persons"));
    const personErrors = Object.fromEntries(personErrorKeys.map((k) => [String_removePrefix(k, "Persons"), errors[k]]));

    const divingLeaderErrorKeys = errorKeys.filter((e) => e.startsWith("DivingLeader."));
    const divingLeaderErrors = Object.fromEntries(
        divingLeaderErrorKeys.map((k) => [String_removePrefix(k, "DivingLeader."), errors[k]])
    );

    const projectLeaderErrorKeys = errorKeys.filter((e) => e.startsWith("ProjectLeader."));
    const projectLeaderErrors = Object.fromEntries(
        projectLeaderErrorKeys.map((k) => [String_removePrefix(k, "ProjectLeader."), errors[k]])
    );

    const updatedFormState = FormState_PopulateErrorMessages(formState, errors);
    updatedFormState.positions = PositionPickerRows_populateErrorMessages(formState.positions, positionErrors);

    updatedFormState.divingLeader = PersonFormElement_populateErrorMessages(formState.divingLeader, divingLeaderErrors);

    updatedFormState.projectLeader = PersonFormElement_populateErrorMessages(
        formState.projectLeader,
        projectLeaderErrors
    );

    updatedFormState.persons = PersonFormElements_populateErrorMessages(formState.persons, personErrors);

    return updatedFormState;
}
